<script setup>
    import { ref, computed } from 'vue';
    import bizumLogo from '@/assets/images/paycomet/apm-bizum.svg';
    import instantCreditLogo from '@/assets/images/paycomet/apm-instantcredit.webp';
    import CardPaymentForm from './CardPaymentForm.vue';
    import BizumPaymentForm from './BizumPaymentForm.vue';
    import InstantCreditPayment from './instant-credit/InstantCreditPayment.vue';

    const props = defineProps({
        methods: {
            type: Array,
            required: true
        },
        jet_id: {
            type: String,
            required: true
        },
        session_id: {
            type: String,
            default: ''
        },
        tipo_venta: {
            type: String,
            default: 'evento'
        },
        esPlazos: {
            type: Boolean,
            default: false
        },
        importeCarrito: {
            type: Number,
            default: 0
        },
        gastosGestionUnitarios: {
            type: Number,
            default: 0
        },
        numAbonados: {
            type: Number,
            default: 1
        },
        store: {
            type: Object,
            default: null
        },
        getVuexAlias: {
            type: Function,
            default: null
        }
    });

    const emit = defineEmits(['payToken', 'bizumError', 'cancel']);

    // The active method. null/'tarjeta' = state A (card form visible).
    // Any other id = state B (that alternative's form replaces the card form).
    const selectedId = ref(null);

    const isCardActive = computed(() => !selectedId.value || selectedId.value === 'tarjeta');

    const alternativeMethods = computed(() => props.methods.filter(m => m.id !== 'tarjeta'));

    // The active method object (used in state B header to show its logo + title).
    const activeMethod = computed(() => props.methods.find(m => m.id === selectedId.value) || null);

    // Logo asset per method id. tarjeta reuses the brand strip in the header.
    const methodLogos = {
        bizum: bizumLogo,
        instant_credit: instantCreditLogo
    };

    const onSelectAlt = id => {
        // Clicking the already-active alternative toggles back to card; clicking a
        // different one switches straight to it without passing through the card.
        selectedId.value = selectedId.value === id ? null : id;
    };

    const onBackToCard = () => {
        selectedId.value = null;
    };
</script>

<template>
    <div
        data-test="payment-step"
        class="tw:bg-white tw:rounded-2xl tw:border tw:border-site/15 tw:overflow-hidden tw:shadow-sm"
    >
        <!-- Header: state A shows the card title + brand logos; state B shows the back link -->
        <div
            data-test="payment-step-header"
            class="tw:px-6 tw:pt-6 tw:pb-4 tw:border-b tw:border-site/10 tw:min-h-[4.5rem] tw:flex tw:items-center"
        >
            <template v-if="isCardActive">
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:flex-wrap tw:w-full">
                    <h2 class="tw:text-2xl tw:font-bold tw:uppercase tw:text-site tw:leading-[1.15] tw:tracking-tight">
                        {{ $t('pago.tarjeta_header_title') }}
                    </h2>
                    <div class="tw:flex tw:items-center tw:gap-1.5 tw:flex-wrap" aria-hidden="true">
                        <img
                            src="@/assets/images/paycomet/apm-visa.svg"
                            alt="Visa"
                            class="tw:h-6 tw:w-auto"
                            loading="lazy"
                        />
                        <img
                            src="@/assets/images/paycomet/apm-mastercard.svg"
                            alt="Mastercard"
                            class="tw:h-6 tw:w-auto"
                            loading="lazy"
                        />
                        <img
                            src="@/assets/images/paycomet/apm-amex.svg"
                            alt="American Express"
                            class="tw:h-6 tw:w-auto"
                            loading="lazy"
                        />
                        <img
                            src="@/assets/images/paycomet/apm-diners.svg"
                            alt="Diners Club"
                            class="tw:h-6 tw:w-auto"
                            loading="lazy"
                        />
                    </div>
                </div>
            </template>
            <div
                v-else
                class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:flex-wrap tw:w-full"
            >
                <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                    <img
                        v-if="activeMethod && methodLogos[activeMethod.id]"
                        :src="methodLogos[activeMethod.id]"
                        :alt="$t(activeMethod.titleKey)"
                        class="tw:h-8 tw:w-auto tw:flex-shrink-0"
                        loading="lazy"
                    />
                    <div class="tw:min-w-0">
                        <h2 class="tw:text-xl tw:font-bold tw:uppercase tw:text-site tw:leading-[1.15] tw:tracking-tight tw:truncate">
                            {{ $t(activeMethod.titleKey) }}
                        </h2>
                        <p class="tw:text-xs tw:text-site/55 tw:truncate">
                            {{ $t(activeMethod.descKey) }}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    data-test="back-to-card"
                    class="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:font-semibold tw:text-site/60 hover:tw:text-site tw:transition-colors tw:uppercase tw:tracking-wider focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-alt focus-visible:tw:ring-offset-2 tw:rounded"
                    @click="onBackToCard"
                >
                    <svg class="tw:w-4 tw:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                    </svg>
                    {{ $t('pago.back_to_card') }}
                </button>
            </div>
        </div>

        <!-- Form zone: swaps between card / bizum / instant-credit with a fade -->
        <div class="tw:px-6 tw:py-5">
            <transition name="step-fade" mode="out-in">
                <CardPaymentForm
                    v-if="isCardActive"
                    key="card"
                    :jet_id="jet_id"
                    :session_id="session_id"
                    :tipo_venta="tipo_venta"
                    :es-plazos="esPlazos"
                    @payToken="token => emit('payToken', token)"
                    @cancel="emit('cancel')"
                />
                <BizumPaymentForm
                    v-else-if="selectedId === 'bizum'"
                    key="bizum"
                    :session_id="session_id"
                    :tipo_venta="tipo_venta"
                    @error="msg => emit('bizumError', msg)"
                />
                <InstantCreditPayment
                    v-else-if="selectedId === 'instant_credit'"
                    key="instant_credit"
                    :session-id="session_id"
                    :tipo-venta="tipo_venta"
                    :importe-carrito="importeCarrito"
                    :gastos-gestion-unitarios="gastosGestionUnitarios"
                    :num-abonados="numAbonados"
                    :store="store"
                    :get-vuex-alias="getVuexAlias"
                />
            </transition>
        </div>

        <!-- Divider question (copy adapts to the state) -->
        <div class="tw:px-6 tw:py-3 tw:border-t tw:border-site/10">
            <p
                data-test="alternative-methods-question"
                class="tw:text-sm tw:font-semibold tw:text-site/80"
            >
                {{ isCardActive ? $t('pago.alternative_methods_question') : $t('pago.alternative_methods_switch') }}
            </p>
        </div>

        <!-- Alternative methods grid (always visible; the active one is highlighted) -->
        <div
            data-test="alternative-methods-grid"
            class="tw:px-6 tw:pb-6 tw:pt-1 tw:grid tw:grid-cols-2 tw:sm:grid-cols-4 tw:gap-3"
        >
            <button
                v-for="alt in alternativeMethods"
                :key="alt.id"
                type="button"
                :data-test="`payment-method-option-${alt.id}`"
                :aria-pressed="selectedId === alt.id ? 'true' : 'false'"
                :class="[
                    'tw:relative tw:flex tw:items-center tw:justify-center tw:py-4 tw:px-3 tw:rounded-xl tw:border-2 tw:transition-all tw:duration-200 tw:cursor-pointer tw:group tw:min-h-[3.25rem] focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-alt focus-visible:tw:ring-offset-2 disabled:tw:opacity-60 disabled:tw:cursor-not-allowed',
                    selectedId === alt.id
                        ? 'tw:border-alt tw:bg-alt tw:shadow-[0_4px_12px_-2px_rgba(209,198,125,0.4)]'
                        : 'tw:border-site/15 tw:bg-white alt-card-unselected'
                ]"
                @click="onSelectAlt(alt.id)"
            >
                <span
                    v-if="selectedId === alt.id"
                    class="tw:absolute tw:top-1.5 tw:right-1.5 tw:w-4 tw:h-4 tw:bg-site tw:text-alt tw:rounded-full tw:flex tw:items-center tw:justify-center tw:shadow-sm"
                    aria-hidden="true"
                >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </span>
                <span class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1.5 tw:min-w-0 tw:w-full">
                    <img
                        v-if="alt.id === 'bizum'"
                        src="@/assets/images/paycomet/apm-bizum.svg"
                        alt="Bizum"
                        class="tw:h-6 tw:w-auto tw:max-w-full"
                        aria-hidden="true"
                        loading="lazy"
                    />
                    <img
                        v-else-if="alt.id === 'instant_credit'"
                        src="@/assets/images/paycomet/apm-instantcredit.webp"
                        alt="Instant Credit"
                        class="tw:h-7 tw:w-auto tw:max-w-full"
                        aria-hidden="true"
                        loading="lazy"
                    />
                    <svg
                        v-else
                        viewBox="0 0 24 24"
                        class="tw:h-6 tw:w-6 tw:text-site/60"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <rect x="3" y="6" width="18" height="13" rx="2" />
                        <line x1="3" y1="11" x2="21" y2="11" />
                    </svg>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-site/75 tw:text-center tw:leading-tight tw:truncate tw:w-full">
                        {{ $t(alt.titleKey) }}
                    </span>
                </span>
            </button>
        </div>

        <!-- Trust strip -->
        <div
            class="tw:px-6 tw:py-3 tw:bg-site/[0.025] tw:border-t tw:border-site/10 tw:flex tw:items-center tw:justify-center tw:gap-3 tw:text-[11px] tw:text-site/55 tw:font-medium tw:flex-wrap"
        >
            <span class="tw:inline-flex tw:items-center tw:gap-1.5 tw:uppercase tw:tracking-wider">
                <svg class="tw:w-3.5 tw:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
                </svg>
                {{ $t('pago.secure') }}
            </span>
            <span class="tw:opacity-40" aria-hidden="true">·</span>
            <span class="tw:inline-flex tw:items-center tw:gap-2">
                <span class="tw:font-semibold tw:text-site/70">{{ $t('pago.partner') }}</span>
                <img
                    :src="require('@/assets/images/paycomet/logo-paycomet-bsabadell-grey.svg')"
                    :alt="$t('pago.partner')"
                    class="tw:h-6 tw:w-auto tw:opacity-75"
                    loading="lazy"
                />
            </span>
        </div>
    </div>
</template>

<style scoped>
.alt-card-unselected {
    transition: border-color 200ms ease, background-color 200ms ease,
        transform 200ms ease, box-shadow 200ms ease;
}

@media (hover: hover) {
    .alt-card-unselected:hover:not(:disabled) {
        border-color: #d1c67d;
        background-color: rgba(209, 198, 125, 0.18);
        transform: translateY(-2px);
        box-shadow: 0 4px 8px -2px rgba(0, 0, 0, 0.08);
    }
}

.step-fade-enter-active,
.step-fade-leave-active {
    transition: opacity 180ms ease, transform 180ms ease;
}

.step-fade-enter-from,
.step-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>
